import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { collection, doc, getDoc, getDocs, limit, query, where } from "firebase/firestore";
import { auth, db } from "../firebaseConfig";

const AdminAccessContext = createContext(null);

export function normalizeAdminRole(role) {
  return String(role || "").trim().toLowerCase();
}

export function AdminAccessProvider({ children }) {
  const [user, authLoading] = useAuthState(auth);
  const [adminRecord, setAdminRecord] = useState(null);
  const [roleLoading, setRoleLoading] = useState(true);

  useEffect(() => {
    let active = true;

    const loadAdminRecord = async () => {
      if (authLoading) return;

      if (!user) {
        if (active) {
          setAdminRecord(null);
          setRoleLoading(false);
        }
        return;
      }

      setRoleLoading(true);

      try {
        const directSnapshot = await getDoc(doc(db, "admin", user.uid));
        let snapshot = directSnapshot.exists() ? directSnapshot : null;

        if (!snapshot) {
          const byUid = await getDocs(
            query(collection(db, "admin"), where("uid", "==", user.uid), limit(1))
          );
          snapshot = byUid.docs[0];
        }

        if (!snapshot && user.email) {
          const byEmail = await getDocs(
            query(collection(db, "admin"), where("email", "==", user.email), limit(1))
          );
          snapshot = byEmail.docs[0];
        }

        if (active) {
          setAdminRecord(snapshot ? { id: snapshot.id, ...snapshot.data() } : null);
        }
      } catch (error) {
        console.error("Failed to resolve admin access:", error);
        if (active) setAdminRecord(null);
      } finally {
        if (active) setRoleLoading(false);
      }
    };

    loadAdminRecord();

    return () => {
      active = false;
    };
  }, [authLoading, user]);

  const value = useMemo(() => {
    const role = normalizeAdminRole(adminRecord?.role);
    return {
      user,
      adminRecord,
      role,
      isMarketing: role === "marketing",
      loading: authLoading || roleLoading,
    };
  }, [adminRecord, authLoading, roleLoading, user]);

  return <AdminAccessContext.Provider value={value}>{children}</AdminAccessContext.Provider>;
}

export function useAdminAccess() {
  const context = useContext(AdminAccessContext);
  if (!context) {
    throw new Error("useAdminAccess must be used inside <AdminAccessProvider>");
  }
  return context;
}
